/* eslint-disable max-lines -- hand-rolled Android-safe markdown renderer; SE-5598 keep-disable directives for the library markdown style maps pushed it over the line budget (sibling ChatContainer carries the same waiver) */
import React, { useMemo } from "react";

import { Box, ScrollView, Text } from "@/components/atoms";

import SafeAndroidMarkdownView from "./SafeAndroidMarkdownView";
import { RADIUS } from "@/config/radius";

/**
 * Props for the SafeAndroidMarkdown component
 */
interface SafeAndroidMarkdownProps {
  text: string;
  isDark: boolean;
}

/**
 * Style configuration for markdown rendering with theme-aware colors
 */
interface MarkdownStyles {
  textColor: string;
  mutedTextColor: string;
  headingColor: string;
  codeBackgroundColor: string;
  codeTextColor: string;
  linkColor: string;
  blockquoteBackground: string;
  blockquoteBorder: string;
  tableBackground: string;
  tableBorder: string;
  isDark: boolean;
}

const renderHeader = (
  line: string,
  level: number,
  index: number,
  headingColor: string
): React.ReactElement | null => {
  const styles: Record<
    number,
    {
      fontSize: number;
      fontWeight:
        | "100"
        | "200"
        | "300"
        | "400"
        | "500"
        | "600"
        | "700"
        | "800"
        | "900"
        | "normal"
        | "bold";
      marginTop: number;
      marginBottom: number;
      letterSpacing: number;
      prefix: number;
    }
  > = {
    1: {
      fontSize: 24,
      fontWeight: "700",
      marginTop: 20,
      marginBottom: 12,
      letterSpacing: -0.5,
      prefix: 2,
    },
    2: {
      fontSize: 20,
      fontWeight: "600",
      marginTop: 16,
      marginBottom: 10,
      letterSpacing: -0.3,
      prefix: 3,
    },
    3: {
      fontSize: 18,
      fontWeight: "600",
      marginTop: 14,
      marginBottom: 8,
      letterSpacing: -0.2,
      prefix: 4,
    },
  };

  const style = styles[level];
  if (!style) return null;

  const { prefix, ...textStyle } = style;
  const headerStyle = { color: headingColor, ...textStyle };

  return (
    <Text
      variant="unstyled"
      key={index}
      // eslint-disable-next-line no-restricted-syntax -- per-level heading font metrics + data-driven theme color; no closed token expresses them
      UNSAFE_style={headerStyle}
    >
      {line.substring(prefix)}
    </Text>
  );
};

const renderBlockquote = (
  line: string,
  index: number,
  styles: MarkdownStyles
): React.ReactNode => {
  const containerStyle = {
    backgroundColor: styles.blockquoteBackground,
    borderLeftColor: styles.blockquoteBorder,
    borderRadius: 6,
    marginVertical: 12,
  };
  const textStyle = {
    color: styles.mutedTextColor,
    fontSize: 16,
    fontStyle: "italic" as const,
    lineHeight: 22,
  };
  return (
    <Box
      key={index}
      className="border-l-4 px-4 py-3"
      // eslint-disable-next-line no-restricted-syntax -- data-driven blockquote background + left-border color; radius:6 off the token ladder + marginVertical spacing (no Stack parent owns it)
      UNSAFE_style={containerStyle}
    >
      <Text
        variant="unstyled"
        // eslint-disable-next-line no-restricted-syntax -- data-driven muted text color + italic typography
        UNSAFE_style={textStyle}
      >
        {line.substring(2)}
      </Text>
    </Box>
  );
};

const renderBulletList = (
  content: string,
  index: number,
  styles: MarkdownStyles
): React.ReactNode => {
  const rowStyle = { marginTop: 4, marginBottom: 4 };
  const bulletStyle = {
    color: styles.isDark ? "#60A5FA" : "#2563EB",
    fontSize: 18,
    fontWeight: "600" as const,
    lineHeight: 22,
    marginRight: 12,
  };
  const contentStyle = {
    color: styles.textColor,
    fontSize: 16,
    lineHeight: 22,
  };
  return (
    <Box
      key={index}
      className="flex-row pl-2"
      // eslint-disable-next-line no-restricted-syntax -- marginVertical:4 list-row rhythm; no-margin rule bars a margin className and no Stack parent owns it
      UNSAFE_style={rowStyle}
    >
      <Text
        variant="unstyled"
        // eslint-disable-next-line no-restricted-syntax -- data-driven bullet glyph color + marginRight spacing
        UNSAFE_style={bulletStyle}
      >
        •
      </Text>
      <Text
        variant="unstyled"
        className="flex-1"
        // eslint-disable-next-line no-restricted-syntax -- data-driven body text color
        UNSAFE_style={contentStyle}
      >
        {processInlineFormatting(
          content,
          styles.linkColor,
          styles.codeBackgroundColor,
          styles.codeTextColor,
          styles.headingColor,
          styles.mutedTextColor
        )}
      </Text>
    </Box>
  );
};

const renderNumberedList = (
  number: string,
  content: string,
  index: number,
  styles: MarkdownStyles
): React.ReactNode => {
  const rowStyle = { marginTop: 4, marginBottom: 4 };
  const numberStyle = {
    color: styles.isDark ? "#60A5FA" : "#2563EB",
    fontSize: 16,
    fontWeight: "600" as const,
    lineHeight: 22,
    marginRight: 12,
  };
  const contentStyle = {
    color: styles.textColor,
    fontSize: 16,
    lineHeight: 22,
  };
  return (
    <Box
      key={index}
      className="flex-row pl-2"
      // eslint-disable-next-line no-restricted-syntax -- marginVertical:4 list-row rhythm; no-margin rule bars a margin className and no Stack parent owns it
      UNSAFE_style={rowStyle}
    >
      <Text
        variant="unstyled"
        className="min-w-6"
        // eslint-disable-next-line no-restricted-syntax -- data-driven list-number color + marginRight spacing
        UNSAFE_style={numberStyle}
      >
        {number}.
      </Text>
      <Text
        variant="unstyled"
        className="flex-1"
        // eslint-disable-next-line no-restricted-syntax -- data-driven body text color
        UNSAFE_style={contentStyle}
      >
        {processInlineFormatting(
          content,
          styles.linkColor,
          styles.codeBackgroundColor,
          styles.codeTextColor,
          styles.headingColor,
          styles.mutedTextColor
        )}
      </Text>
    </Box>
  );
};

const renderHorizontalRule = (
  index: number,
  isDark: boolean
): React.ReactNode => {
  const ruleStyle = {
    backgroundColor: isDark ? "#374151" : "#E5E7EB",
    marginVertical: 20,
    marginHorizontal: 8,
  };
  return (
    <Box
      key={index}
      className="h-px"
      // eslint-disable-next-line no-restricted-syntax -- data-driven divider color + marginVertical/Horizontal spacing (no Stack parent owns it)
      UNSAFE_style={ruleStyle}
    />
  );
};

const renderCodeBlock = (
  line: string,
  index: number,
  styles: MarkdownStyles,
  isDark: boolean
): React.ReactElement => {
  const code = line.substring(3);
  const containerStyle = {
    backgroundColor: styles.codeBackgroundColor,
    borderLeftWidth: 3,
    borderLeftColor: isDark ? "#F59E0B" : "#DC2626",
    marginVertical: 12,
  };
  const codeStyle = {
    color: styles.codeTextColor,
    fontFamily: "monospace",
    fontSize: 13,
    lineHeight: 18,
    letterSpacing: 0.5,
  };
  return (
    <Box
      key={index}
      className="rounded-md p-4"
      // eslint-disable-next-line no-restricted-syntax -- data-driven code background + off-ladder borderLeftWidth:3 + data-driven accent border color
      UNSAFE_style={containerStyle}
    >
      <Text
        variant="unstyled"
        // eslint-disable-next-line no-restricted-syntax -- data-driven code text color + monospace + letterSpacing
        UNSAFE_style={codeStyle}
      >
        {code}
      </Text>
    </Box>
  );
};

/**
 * Processes a single markdown line and converts it to a React Native component.
 * This helper function extracts the line processing logic from the useMemo hook
 * to reduce cognitive complexity while maintaining clarity of markdown pattern matching.
 * @param props - The processing configuration
 * @param props.line - The markdown line text to process
 * @param props.lineIndex - The index of the line in the document for keying
 * @param props.styles - The MarkdownStyles configuration for theme-aware rendering
 * @param props.onLinkPress - Optional callback for link press events
 * @returns The processed React Native component for the markdown line
 */
function processMarkdownLine(
  props: Readonly<{
    line: string;
    lineIndex: number;
    styles: MarkdownStyles;
    onLinkPress?: (url: string) => void;
  }>
): React.ReactNode {
  const { line, lineIndex, styles } = props;

  if (!line.trim()) {
    return <Box key={`space-${lineIndex}`} className="h-3" />;
  }

  if (line.startsWith("# ")) {
    return renderHeader(line, 1, lineIndex, styles.headingColor);
  }

  if (line.startsWith("## ")) {
    return renderHeader(line, 2, lineIndex, styles.headingColor);
  }

  if (line.startsWith("### ")) {
    return renderHeader(line, 3, lineIndex, styles.headingColor);
  }

  if (line.startsWith("> ")) {
    return renderBlockquote(line, lineIndex, styles);
  }

  if (/^[*-]\s+/.exec(line)) {
    const content = line.replace(/^[*-]\s+/, "");
    return renderBulletList(content, lineIndex, styles);
  }

  const numberMatch = /^(\d+)\.\s+([^\n]{0,1000})$/.exec(line);
  if (numberMatch) {
    return renderNumberedList(
      numberMatch[1],
      numberMatch[2],
      lineIndex,
      styles
    );
  }

  if (/^(-{3,}|\*{3,}|_{3,})$/.exec(line)) {
    return renderHorizontalRule(lineIndex, styles.isDark);
  }

  if (line.includes("|") && !/^[-\s|:]+$/.exec(line)) {
    return null;
  }

  if (line.startsWith("```") && line.length > 3) {
    return renderCodeBlock(line, lineIndex, styles, styles.isDark);
  }

  const paragraphStyle = {
    color: styles.textColor,
    fontSize: 16,
    lineHeight: 24,
    marginVertical: 6,
    paddingHorizontal: 2,
  };
  return (
    <Text
      variant="unstyled"
      key={lineIndex}
      // eslint-disable-next-line no-restricted-syntax -- data-driven paragraph text color; marginVertical:6 off the token ladder
      UNSAFE_style={paragraphStyle}
    >
      {processInlineFormatting(
        line,
        styles.linkColor,
        styles.codeBackgroundColor,
        styles.codeTextColor,
        styles.headingColor,
        styles.mutedTextColor
      )}
    </Text>
  );
}

/**
 * A safe markdown renderer for Android that uses custom React Native components
 * instead of webview-based markdown libraries to avoid Android-specific crashes
 * @param root0 - Component props
 * @param root0.text - Markdown text content to render
 * @param root0.isDark - Whether dark theme is enabled
 */
const SafeAndroidMarkdownContainer: React.FC<SafeAndroidMarkdownProps> = ({
  text,
  isDark,
}) => {
  const styles: MarkdownStyles = useMemo(
    () => ({
      textColor: isDark ? "#F9FAFB" : "#111827",
      mutedTextColor: isDark ? "#D1D5DB" : "#6B7280",
      headingColor: isDark ? "#FFFFFF" : "#1F2937",
      codeBackgroundColor: isDark ? "#1F2937" : "#F8FAFC",
      codeTextColor: isDark ? "#F59E0B" : "#DC2626",
      linkColor: isDark ? "#60A5FA" : "#2563EB",
      blockquoteBackground: isDark ? "#1F2937" : "#F8FAFC",
      blockquoteBorder: isDark ? "#3B82F6" : "#6366F1",
      tableBackground: isDark ? "#1F2937" : "#FAFAFA",
      tableBorder: isDark ? "#374151" : "#E5E7EB",
      isDark,
    }),
    [isDark]
  );

  const processedContent = useMemo(() => {
    const lines = text.split("\n");

    const processLinesRecursively = (
      linesArray: string[],
      startIndex: number
    ): React.ReactNode[] => {
      if (startIndex >= linesArray.length) {
        return [];
      }

      const line = linesArray[startIndex];

      // Check if this is the start of a table
      if (line.includes("|") && !/^[-\s|:]+$/.exec(line)) {
        const tableResult = processTable(linesArray, startIndex, {
          tableBackground: styles.tableBackground,
          tableBorder: styles.tableBorder,
          headingColor: styles.headingColor,
          textColor: styles.textColor,
          isDark: styles.isDark,
          textColorParam: styles.textColor,
          linkColor: styles.linkColor,
          codeBackgroundColor: styles.codeBackgroundColor,
          codeTextColor: styles.codeTextColor,
          mutedTextColor: styles.mutedTextColor,
        });

        if (tableResult.tableElement) {
          return [
            tableResult.tableElement,
            ...processLinesRecursively(linesArray, tableResult.nextIndex),
          ];
        }
      }

      // Process regular line
      const processedLine = processMarkdownLine({
        line,
        lineIndex: startIndex,
        styles,
      });
      const remainingLines = processLinesRecursively(
        linesArray,
        startIndex + 1
      );

      return processedLine
        ? [processedLine, ...remainingLines]
        : remainingLines;
    };

    return processLinesRecursively(lines, 0);
  }, [text, styles]);

  return <SafeAndroidMarkdownView processedContent={processedContent} />;
};

SafeAndroidMarkdownContainer.displayName = "SafeAndroidMarkdownContainer";

export default React.memo(SafeAndroidMarkdownContainer);

// Export for testing
export { processMarkdownLine };

/**
 * Prepends a before-text segment to a node array when non-empty
 * @param before - Optional text to prepend as a string node
 */
function prependText(before: string): string[] {
  return before ? [before] : [];
}

/**
 * Processes inline markdown formatting (bold, italic, code, links) within text
 * @param text - The raw text content to process for inline markdown formatting
 * @param linkColor - The color value to apply to markdown hyperlinks
 * @param codeBackgroundColor - The background color for inline code blocks
 * @param codeTextColor - The text color for inline code content
 * @param headingColor - The color to apply to bold formatted text (markdown **)
 * @param mutedTextColor - The color to apply to italic formatted text (markdown *)
 */
function processInlineFormatting(
  text: string,
  linkColor: string,
  codeBackgroundColor: string,
  codeTextColor: string,
  headingColor: string,
  mutedTextColor: string
): React.ReactNode[] {
  const processText = (
    remaining: string,
    keyIndex: number
  ): React.ReactNode[] => {
    if (remaining.length === 0) return [];

    // Check for bold **text** - safe regex with bounded repetition
    const boldMatch = /\*\*([^*\n]{1,100}?)\*\*/.exec(remaining);
    if (boldMatch && boldMatch.index !== undefined) {
      const before = remaining.substring(0, boldMatch.index);
      const after = remaining.substring(boldMatch.index + boldMatch[0].length);
      const boldStyle = { fontWeight: "700" as const, color: headingColor };

      return [
        ...prependText(before),
        <Text
          variant="unstyled"
          key={`bold-${keyIndex}`}
          // eslint-disable-next-line no-restricted-syntax -- data-driven bold text color
          UNSAFE_style={boldStyle}
        >
          {boldMatch[1]}
        </Text>,
        ...processText(after, keyIndex + 1),
      ];
    }

    // Check for italic *text* - safe regex with bounded repetition
    const italicMatch = /\*([^*\n]{1,100}?)\*/.exec(remaining);
    if (italicMatch && italicMatch.index !== undefined) {
      const before = remaining.substring(0, italicMatch.index);
      const after = remaining.substring(
        italicMatch.index + italicMatch[0].length
      );
      const italicStyle = {
        fontStyle: "italic" as const,
        color: mutedTextColor,
      };

      return [
        ...prependText(before),
        <Text
          variant="unstyled"
          key={`italic-${keyIndex}`}
          // eslint-disable-next-line no-restricted-syntax -- data-driven italic text color
          UNSAFE_style={italicStyle}
        >
          {italicMatch[1]}
        </Text>,
        ...processText(after, keyIndex + 1),
      ];
    }

    // Check for inline code `code` - safe regex with bounded repetition
    const codeMatch = /`([^`\n]{1,200}?)`/.exec(remaining);
    if (codeMatch && codeMatch.index !== undefined) {
      const before = remaining.substring(0, codeMatch.index);
      const after = remaining.substring(codeMatch.index + codeMatch[0].length);
      const codeStyle = {
        backgroundColor: codeBackgroundColor,
        color: codeTextColor,
        fontFamily: "monospace",
        fontSize: 14,
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: RADIUS.sm,
        fontWeight: "500" as const,
      };

      return [
        ...prependText(before),
        <Text
          variant="unstyled"
          key={`code-${keyIndex}`}
          // eslint-disable-next-line no-restricted-syntax -- data-driven inline-code background/text color + monospace + off-ladder padding
          UNSAFE_style={codeStyle}
        >
          {codeMatch[1]}
        </Text>,
        ...processText(after, keyIndex + 1),
      ];
    }

    // Check for links [text](url) - safe regex with bounded repetition
    const linkMatch = /\[([^\]\n]{1,100}?)\]\(([^)\n]{1,200}?)\)/.exec(
      remaining
    );
    if (linkMatch && linkMatch.index !== undefined) {
      const before = remaining.substring(0, linkMatch.index);
      const after = remaining.substring(linkMatch.index + linkMatch[0].length);
      const linkStyle = {
        color: linkColor,
        fontWeight: "500" as const,
      };

      return [
        ...prependText(before),
        <Text
          variant="unstyled"
          key={`link-${keyIndex}`}
          className="underline"
          // eslint-disable-next-line no-restricted-syntax -- data-driven link color
          UNSAFE_style={linkStyle}
        >
          {linkMatch[1]}
        </Text>,
        ...processText(after, keyIndex + 1),
      ];
    }

    return [remaining];
  };

  return processText(text, 0);
}

/**
 * Processes markdown tables and converts them to React Native components
 * @param lines - Array of all markdown lines being processed
 * @param startIndex - The starting index in the lines array where the table begins
 * @param config - Styling configuration object for table rendering
 * @param config.tableBackground - Background color for the table container
 * @param config.tableBorder - Border color for table cells and container
 * @param config.headingColor - Text color for table header row cells
 * @param config.textColor - Text color for table body row cells
 * @param config.isDark - Whether dark theme mode is currently enabled
 * @param config.textColorParam - Additional text color parameter for backward compatibility
 * @param config.linkColor - Color for hyperlinks within table cells
 * @param config.codeBackgroundColor - Background color for inline code within table cells
 * @param config.codeTextColor - Text color for inline code within table cells
 * @param config.mutedTextColor - Color for italic/muted text within table cells
 */
function processTable(
  lines: string[],
  startIndex: number,
  config: {
    tableBackground: string;
    tableBorder: string;
    headingColor: string;
    textColor: string;
    isDark: boolean;
    textColorParam: string;
    linkColor: string;
    codeBackgroundColor: string;
    codeTextColor: string;
    mutedTextColor: string;
  }
): { tableElement: React.ReactNode | null; nextIndex: number } {
  const collectTableRows = (
    linesArray: string[],
    currentIndex: number
  ): { rows: string[]; nextIndex: number } => {
    if (currentIndex >= linesArray.length) {
      return { rows: [], nextIndex: currentIndex };
    }

    const line = linesArray[currentIndex];

    // If it's a table separator line (|---|---|), skip it
    if (/^[-\s|:]+$/.exec(line)) {
      return collectTableRows(linesArray, currentIndex + 1);
    }

    // If it contains pipes and is not empty, it's a table row
    if (line.includes("|") && line.trim()) {
      const remainingResult = collectTableRows(linesArray, currentIndex + 1);
      return {
        rows: [line, ...remainingResult.rows],
        nextIndex: remainingResult.nextIndex,
      };
    }

    // End of table
    return { rows: [], nextIndex: currentIndex };
  };

  const { rows: tableRows, nextIndex } = collectTableRows(lines, startIndex);

  if (tableRows.length === 0) {
    return { tableElement: null, nextIndex };
  }

  // Process table rows into cells
  const processedRows = tableRows.map(row => {
    return row
      .split("|")
      .map(cell => cell.trim())
      .filter((cell, index, arr) => {
        // Remove empty first/last cells that are common in markdown tables
        return (
          !(index === 0 && cell === "") &&
          !(index === arr.length - 1 && cell === "")
        );
      });
  });

  const isHeaderRow = (rowIndex: number) => rowIndex === 0;

  const scrollStyle = { marginVertical: 12 };
  const tableStyle = {
    backgroundColor: config.tableBackground,
    borderColor: config.tableBorder,
    minWidth: 400,
  };
  const tableElement = (
    <ScrollView
      key={`table-${startIndex}`}
      horizontal
      showsHorizontalScrollIndicator={false}
      // eslint-disable-next-line no-restricted-syntax -- ScrollView atom is a bare re-export without an UNSAFE_style prop; marginVertical:12 spacer and the no-margin rule bars a margin className
      style={scrollStyle}
    >
      <Box
        className="overflow-hidden rounded-md border"
        // eslint-disable-next-line no-restricted-syntax -- data-driven table background/border color + off-ladder minWidth:400
        UNSAFE_style={tableStyle}
      >
        {processedRows.map((cells, rowIndex) => {
          const rowStyle = {
            borderBottomWidth: rowIndex < processedRows.length - 1 ? 1 : 0,
            borderBottomColor: config.tableBorder,
            backgroundColor: isHeaderRow(rowIndex)
              ? config.isDark
                ? "#374151"
                : "#F3F4F6"
              : "transparent",
          };
          return (
            <Box
              key={rowIndex}
              className="flex-row"
              // eslint-disable-next-line no-restricted-syntax -- row-index-computed bottom border width + data-driven border/background color
              UNSAFE_style={rowStyle}
            >
              {cells.map((cell, cellIndex) => {
                const cellStyle = {
                  width: 100,
                  paddingVertical: 10,
                  borderRightWidth: cellIndex < cells.length - 1 ? 1 : 0,
                  borderRightColor: config.tableBorder,
                };
                const cellTextStyle = {
                  color: isHeaderRow(rowIndex)
                    ? config.headingColor
                    : config.textColor,
                  fontSize: 14,
                  fontWeight: isHeaderRow(rowIndex)
                    ? ("600" as const)
                    : ("400" as const),
                  lineHeight: 18,
                };
                return (
                  <Box
                    key={cellIndex}
                    className="justify-center px-3"
                    // eslint-disable-next-line no-restricted-syntax -- off-ladder cell width:100 + paddingVertical:10 + cell-index-computed right border width + data-driven border color
                    UNSAFE_style={cellStyle}
                  >
                    <Text
                      variant="unstyled"
                      className="text-left"
                      // eslint-disable-next-line no-restricted-syntax -- header/body conditional data-driven text color + weight
                      UNSAFE_style={cellTextStyle}
                    >
                      {cell}
                    </Text>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </ScrollView>
  );

  return { tableElement, nextIndex };
}

/* eslint-enable max-lines -- re-enable after the file-level SE-5598 waiver above */
